/**
 * Shared date/currency formatters. The trip pages each used to carry their
 * own copy of these (including the "T12:00:00" local-noon parse that dodges
 * UTC day-shift), and the two sync bars each had their own relative-time
 * formatter.
 */

/** Parse a YYYY-MM-DD string as local noon — immune to timezone day-shift. */
export function parseLocalDate(iso: string): Date {
  return new Date(iso + "T12:00:00");
}

/** "Sat, May 9" — the day header format used across the trip tabs. */
export function formatDay(iso: string): string {
  return parseLocalDate(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "May 9, 2026" */
export function formatFullDate(iso: string): string {
  return parseLocalDate(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** "May 9, 2026 → May 17, 2026" */
export function formatDateRange(startIso: string, endIso: string): string {
  return `${formatFullDate(startIso)} → ${formatFullDate(endIso)}`;
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" */
export function timeAgo(from: Date | number, now: number = Date.now()): string {
  const ts = typeof from === "number" ? from : from.getTime();
  const min = Math.floor((now - ts) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** "$1,234" (whole dollars — trip budget amounts are stored as integers). */
export function formatCurrency(amount: number, currency = "USD"): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
}
